import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';

import type { PaginationResult } from '@apple/utils/pagination/models';

import { getQueueMessage, getQueueMessageLogs, getQueueMessages, getQueues } from './api';
import type {
	QueueFilters,
	QueueMessageId,
	QueueMessageListing,
	QueueStatus,
	QueueType,
} from './models';

export const queueMessageQueryKeys = {
	all: ['queue-messages'] as const,
	queues: (filters: QueueFilters = {}) =>
		[...queueMessageQueryKeys.all, 'queues', filters] as const,
	queue: (queue: QueueType) => [...queueMessageQueryKeys.all, 'queue', queue] as const,
	list: (
		queue: QueueType,
		status: QueueStatus,
		pagination: PaginationState,
		filters: QueueFilters,
	) => [...queueMessageQueryKeys.queue(queue), 'status', status, pagination, filters] as const,
	detail: (messageId: QueueMessageId) =>
		[...queueMessageQueryKeys.all, 'message', messageId] as const,
	logs: (messageId: QueueMessageId) =>
		[...queueMessageQueryKeys.detail(messageId), 'logs'] as const,
};

export const queueMessageQueries = {
	getQueues: (filters: QueueFilters = {}) =>
		queryOptions({
			queryKey: queueMessageQueryKeys.queues(filters),
			queryFn: ({ signal }) => getQueues(filters, signal),
			staleTime: 30 * 1000,
		}),

	getQueueMessages: (
		queue: QueueType,
		status: QueueStatus,
		pagination: PaginationState,
		filters: QueueFilters = {},
	) =>
		queryOptions<PaginationResult<QueueMessageListing>>({
			queryKey: queueMessageQueryKeys.list(queue, status, pagination, filters),
			queryFn: ({ signal }) => getQueueMessages(queue, status, pagination, filters, signal),
			placeholderData: keepPreviousData,
			initialData: {
				currentPage: pagination.pageIndex + 1,
				pageSize: pagination.pageSize,
				totalCount: 0,
				results: [],
			},
			initialDataUpdatedAt: 0,
			staleTime: 30 * 1000,
		}),

	getQueueMessage: (messageId: QueueMessageId) =>
		queryOptions({
			queryKey: queueMessageQueryKeys.detail(messageId),
			queryFn: ({ signal }) => getQueueMessage(messageId, signal),
			staleTime: 30 * 1000,
		}),

	getQueueMessageLogs: (messageId: QueueMessageId) =>
		queryOptions({
			queryKey: queueMessageQueryKeys.logs(messageId),
			queryFn: ({ signal }) => getQueueMessageLogs(messageId, signal),
			staleTime: 30 * 1000,
		}),
} as const;
